/**
 * Check if planogram is complete: all visible cells (including merged blocks) have products
 * @param {Object} data - Planogram data object with cells, rows, and columns
 * @returns {boolean} - True if all visible cells have products, false otherwise
 */
export const isPlanogramComplete = (data) => {
    if (!data?.cells || !Number.isInteger(data?.rows) || !Number.isInteger(data?.columns)) {
        return false;
    }

    for (let rowIndex = 0; rowIndex < data.rows; rowIndex++) {
        const row = data.cells[rowIndex];
        if (!row) return false;

        let colIndex = 0;
        while (colIndex < data.columns) {
            const cell = row[colIndex];
            if (!cell) return false;

            // Consider only visible cells (start of a block)
            if (cell.merged && cell.mergeData?.isMerged) {
                // Defensive: this would mean we started inside a merged block
                return false;
            }

            const hasProduct = cell.type === 'product' && !!cell.product;
            if (!hasProduct) return false;

            const span = (cell.merged && cell.mergeData?.colspan > 1) ? cell.mergeData.colspan : 1;
            colIndex += span; // jump over covered columns of this merged block
        }
    }

    return true;
};

export const ROW_DIRECTION_T2B = 'T2B';
export const ROW_DIRECTION_B2T = 'B2T';
export const SLOT_DIRECTION_L2R = 'L2R';
export const SLOT_DIRECTION_R2L = 'R2L';

const buildRange = (length, reversed) => {
    const arr = Array.from({ length }, (_, i) => i);
    return reversed ? arr.reverse() : arr;
};

const buildVisibleSequence = (cells, rows, columns, rowOrder, colOrder) => {
    const sequence = [];
    const visitedKeys = new Set();

    for (const r of rowOrder) {
        for (const c of colOrder) {
            const cell = cells[r]?.[c];
            if (!cell) continue;

            let sourceR = r;
            let sourceC = c;
            if (cell.merged && cell.mergeData?.isMerged) {
                sourceR = cell.mergeData.sourceCell.rowIndex;
                sourceC = cell.mergeData.sourceCell.colIndex;
            }

            const sourceKey = `${sourceR}-${sourceC}`;
            if (visitedKeys.has(sourceKey)) continue;

            visitedKeys.add(sourceKey);
            const sourceCell = cells[sourceR]?.[sourceC];
            const colspan = (sourceCell?.merged && sourceCell.mergeData?.colspan > 1) ? sourceCell.mergeData.colspan : 1;
            for (let i = 0; i < colspan; i++) {
                visitedKeys.add(`${sourceR}-${sourceC + i}`);
            }

            sequence.push({ rowIndex: sourceR, colIndex: sourceC });
        }
    }

    return sequence;
};

/**
 * Writes sequential MDB IDs into every cell in `sequence`, starting at
 * `startId` and stepping by `increment` between each PHYSICAL slot —
 * including the sub-positions inside a merged (multi-slot) cell.
 *
 * *** FIX: `increment` used to be completely ignored — this function's
 * signature didn't even accept it, and the step between slots was
 * hardcoded to `colspan` (i.e. always +1 per physical slot regardless of
 * whatever "MDB ID Increment" the person configured in the dialog). That
 * meant InitializationDialog's `mdbIdIncrement` field had no effect
 * whatsoever on the actual assignment — it visibly changed the dialog's
 * own "Ending MDB ID" preview (since that preview runs this same
 * calculation) NO differently than leaving it at 1, because both paths
 * used the same broken function. ***
 *
 * A merged cell spanning `colspan` physical slots still gets `colspan`
 * consecutive IDs (currentId, currentId+increment, currentId+2*increment,
 * ...) for its own sub-positions — those are physically adjacent hardware
 * slots wired together, not separate product positions, so they always
 * step by `increment` too, same as any other slot-to-slot step. The
 * running counter then advances by `colspan * increment` to reach the
 * next visible block's starting ID.
 */
const assignIdsToSequence = (cells, sequence, startId, increment = 1) => {
    let currentId = startId;

    for (const { rowIndex, colIndex } of sequence) {
        const cell = cells[rowIndex]?.[colIndex];
        if (!cell) continue;

        const colspan = (cell.merged && cell.mergeData?.colspan > 1) ? cell.mergeData.colspan : 1;

        cell.mdbId = String(currentId);

        if (colspan > 1) {
            const mdbIds = Array.from({ length: colspan }, (_, i) => String(currentId + i * increment));
            cell.mergedMdbIds = mdbIds;
            if (cell.mergeData) {
                cell.mergeData.mergedMdbIds = mdbIds;
            }
            for (let i = 1; i < colspan; i++) {
                const hidden = cells[rowIndex]?.[colIndex + i];
                if (hidden?.mergeData) {
                    hidden.mergeData.mergedMdbIds = mdbIds;
                }
            }
        }

        currentId += colspan * increment;
    }

    return currentId;
};

/**
 * Calculate automatic MDB ID assignment for a planogram grid.
 *
 * @param {Object} planogramData - { cells, rows, columns }
 * @param {Object} config
 * @param {number|string} config.startMdbId - starting MDB ID integer
 * @param {string} config.rowDirection - 'T2B' (top-to-bottom) or 'B2T' (bottom-to-top)
 * @param {string} config.slotDirection - 'L2R' (left-to-right) or 'R2L' (right-to-left)
 * @param {boolean} [config.customStartEnabled] - whether to use custom start coordinates
 * @param {number} [config.startRow] - custom start row (0-based)
 * @param {number} [config.startCol] - custom start column (0-based)
 * @param {number|string} [config.mdbIdIncrement] - step between consecutive MDB IDs (defaults to 1)
 * @returns {{ cells: Array, endingMdbId: number }}
 */
export const calculateAutoMdbAssignment = (planogramData, config) => {
    const {
        startMdbId,
        rowDirection = ROW_DIRECTION_T2B,
        slotDirection = SLOT_DIRECTION_L2R,
        customStartEnabled = false,
        startRow,
        startCol,
        // *** FIX: this was missing from the destructure entirely, so it
        // was silently dropped no matter what the dialog sent through. ***
        mdbIdIncrement
    } = config;

    const rows = planogramData.rows;
    const columns = planogramData.columns;

    const cells = JSON.parse(JSON.stringify(planogramData.cells));

    let currentId = parseInt(startMdbId, 10);
    if (isNaN(currentId) || currentId < 1) {
        currentId = 1;
    }

    // *** FIX: parse + validate the increment the same defensive way
    // startMdbId already was, defaulting to 1 (the previous implicit
    // behavior) whenever it's missing, non-numeric, or less than 1 —
    // negative/zero increments would either freeze or reverse the
    // sequence, neither of which makes sense for an MDB ID assignment. ***
    let increment = parseInt(mdbIdIncrement, 10);
    if (isNaN(increment) || increment < 1) {
        increment = 1;
    }

    const rowOrder = buildRange(rows, rowDirection === ROW_DIRECTION_B2T);
    const colOrder = buildRange(columns, slotDirection === SLOT_DIRECTION_R2L);

    const allVisibleCells = buildVisibleSequence(cells, rows, columns, rowOrder, colOrder);

    let forwardSequence = [...allVisibleCells];

    // *** FIX: this used to be `typeof startRow === 'number' && typeof
    // startCol === 'number'`. If the dialog's Row/Cell dropdowns store
    // their selected option's `value` as a string (e.g. PrimeReact
    // Dropdown options built as `{ label: '2', value: '2' }`, which is a
    // very common pattern), startRow/startCol arrive here as strings —
    // "2", not 2. `typeof "2" === 'number'` is false, so this entire
    // custom-start branch was silently skipped every time, and
    // forwardSequence quietly fell back to the full top-left-first
    // sequence set up above — exactly the "always initializes from the
    // start" symptom, even though the dialog's own UI showed a Row/Cell/
    // direction that looked correctly selected. Coercing with Number(...)
    // and validating with Number.isInteger(...) + a grid-bounds check
    // makes this work the same whether startRow/startCol arrive as
    // numbers or numeric strings, and safely no-ops (falls back to the
    // full sequence, same as before) for genuinely invalid input instead
    // of throwing. ***
    const parsedStartRow = Number(startRow);
    const parsedStartCol = Number(startCol);
    const hasValidCustomStart =
        customStartEnabled &&
        Number.isInteger(parsedStartRow) &&
        Number.isInteger(parsedStartCol) &&
        parsedStartRow >= 0 && parsedStartRow < rows &&
        parsedStartCol >= 0 && parsedStartCol < columns;

    if (hasValidCustomStart) {
        const cell = cells[parsedStartRow]?.[parsedStartCol];
        let snappedRow = parsedStartRow;
        let snappedCol = parsedStartCol;
        if (cell?.merged && cell.mergeData?.isMerged) {
            snappedRow = cell.mergeData.sourceCell.rowIndex;
            snappedCol = cell.mergeData.sourceCell.colIndex;
        }

        const startIdx = allVisibleCells.findIndex(
            c => c.rowIndex === snappedRow && c.colIndex === snappedCol
        );

        if (startIdx !== -1) {
            forwardSequence = allVisibleCells.slice(startIdx);
        }
    }

    let runningId = assignIdsToSequence(cells, forwardSequence, currentId, increment);

    if (customStartEnabled && forwardSequence.length < allVisibleCells.length) {
        const assignedKeys = new Set(forwardSequence.map(c => `${c.rowIndex}-${c.colIndex}`));
        const wrapSequence = allVisibleCells.filter(
            c => !assignedKeys.has(`${c.rowIndex}-${c.colIndex}`)
        );
        if (wrapSequence.length > 0) {
            runningId = assignIdsToSequence(cells, wrapSequence, runningId, increment);
        }
    }

    // *** FIX: was hardcoded `runningId - 1` (correct only when
    // increment === 1) — now generalized to subtract the actual step so
    // "Ending MDB ID" reflects the true last-assigned value for any
    // increment. ***
    const endingMdbId = runningId - increment;

    return { cells, endingMdbId };
};